import { api, unwrapList } from '../../../shared/lib/axios';

export type SupplierType = 'pessoa_juridica' | 'pessoa_fisica';
export type SupplierStatus =
  | 'em_cadastro'
  | 'pendente_documentacao'
  | 'em_analise'
  | 'em_homologacao'
  | 'homologado'
  | 'homologado_restricao'
  | 'reprovado'
  | 'suspenso'
  | 'inativo';
export type SupplierRiskLevel = 'baixo' | 'medio' | 'alto' | 'critico';
export type HomologationStep =
  | 'analise_documental'
  | 'validacao_fiscal'
  | 'validacao_financeira'
  | 'validacao_juridica'
  | 'validacao_compliance'
  | 'aprovacao_final';
export type SupplierApprovalDecision = 'aprovado' | 'aprovado_restricao' | 'reprovado';
export type SupplierDocumentStatus =
  | 'enviado'
  | 'aprovado'
  | 'reprovado'
  | 'vencido'
  | 'a_vencer'
  | 'atualizacao_necessaria';
export type SupplierEvaluationResult = 'excelente' | 'aprovado' | 'aprovado_restricao' | 'reprovado';
export type SupplierDocumentValidityRule =
  | 'with_expiration'
  | 'no_expiration'
  | 'periodic_update'
  | 'replace_on_change';
export type SupplierPixKeyType = 'cpf' | 'cnpj' | 'email' | 'phone' | 'random';
export type SupplierBlockingCategory =
  | 'document_pending'
  | 'homologation_pending'
  | 'rejection'
  | 'expired_document'
  | 'blocking_risk'
  | 'registration_pending'
  | 'administrative_block'
  | 'homologation_expired'
  | 'other';

export interface SupplierDocumentType {
  code: string;
  name: string;
  validityRule: SupplierDocumentValidityRule;
  requiredForCorporate: boolean;
  requiredForIndividual: boolean;
  updateIntervalDays?: number | null;
  blocksOnExpiration: boolean;
  active: boolean;
}

export interface SupplierBlockingCondition {
  code: string;
  category: SupplierBlockingCategory;
  message: string;
  sourceType?: string | null;
  sourceId?: string | null;
  action?: string | null;
}

export interface SupplierDocument {
  id: string;
  supplierId: string;
  documentType: string;
  name: string;
  fileUrl: string | null;
  fileName: string;
  fileSize: number;
  mimeType?: string | null;
  version: number;
  expiresAt?: string | null;
  issuedAt?: string | null;
  isCurrent?: boolean;
  validityRule?: SupplierDocumentValidityRule | null;
  validityStatus?: string | null;
  status: SupplierDocumentStatus;
  rejectionReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierApproval {
  id: string;
  supplierId: string;
  step: HomologationStep;
  decision: SupplierApprovalDecision;
  justification?: string | null;
  approvedByName?: string | null;
  approvedByRole?: string | null;
  approvedAt: string;
  validUntil?: string | null;
}

export interface SupplierEvaluation {
  id: string;
  supplierId: string;
  qualityScore: number;
  deadlineScore: number;
  serviceScore: number;
  complianceScore: number;
  priceScore: number;
  slaScore: number;
  averageScore: number;
  result: SupplierEvaluationResult;
  comments?: string | null;
  evaluatedByName?: string | null;
  createdAt: string;
}

export interface SupplierHistory {
  id: string;
  supplierId: string;
  action: string;
  userName?: string | null;
  oldValue?: Record<string, unknown> | null;
  newValue?: Record<string, unknown> | null;
  createdAt: string;
}

export interface Supplier {
  id: string;
  supplierType: SupplierType;
  documentNumber: string;
  corporateName: string;
  tradeName?: string | null;
  stateRegistration?: string | null;
  municipalRegistration?: string | null;
  taxRegime?: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  bankName?: string | null;
  bankCode?: string | null;
  bankAgency?: string | null;
  bankAccount?: string | null;
  bankAccountType?: string | null;
  bankPixKeyType?: SupplierPixKeyType | null;
  bankPixKey?: string | null;
  addressZipCode?: string | null;
  addressStreet?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  addressDistrict?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressCountry?: string | null;
  categories: string[];
  services: string[];
  status: SupplierStatus;
  homologationStep: HomologationStep;
  riskLevel: SupplierRiskLevel;
  riskScore: number;
  averageRating: number;
  notes?: string | null;
  administrativeBlockReason?: string | null;
  documents: SupplierDocument[];
  approvals: SupplierApproval[];
  evaluations: SupplierEvaluation[];
  history: SupplierHistory[];
  requiredDocuments: string[];
  pendingRequiredDocuments: string[];
  purchaseEligible: boolean;
  blockingReasons: string[];
  blockingConditions: SupplierBlockingCondition[];
  homologatedAt?: string | null;
  homologationValidUntil?: string | null;
  homologatedBy?: string | null;
  homologatedByName?: string | null;
  legacyHomologationWithoutValidity?: boolean;
  documentSummary: {
    total: number;
    expired: number;
    expiring: number;
    pending: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface SupplierDashboard {
  totalSuppliers: number;
  activeSuppliers: number;
  homologatedSuppliers: number;
  pendingSuppliers: number;
  expiredCertificates: number;
  expiringCertificates: number;
  suppliersByCategory: Record<string, number>;
  avgHomologationTimeDays: number;
  blockedSuppliers: number;
  averageSupplierRating: number;
}

export interface SupplierPortalNotification {
  id: string;
  userId: string;
  type: string;
  payload?: Record<string, unknown> | null;
  readAt?: string | null;
  createdAt: string;
}

export interface SupplierListResponse {
  data: Supplier[];
  pagination?: { limit: number; offset: number; count: number };
}

export interface CreateSupplierPayload {
  supplierType: SupplierType;
  documentNumber: string;
  corporateName: string;
  tradeName?: string;
  stateRegistration?: string;
  municipalRegistration?: string;
  taxRegime?: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  bankName?: string;
  bankCode?: string;
  bankAgency?: string;
  bankAccount?: string;
  bankAccountType?: string;
  bankPixKeyType?: SupplierPixKeyType;
  bankPixKey?: string;
  addressZipCode?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressDistrict?: string;
  addressCity?: string;
  addressState?: string;
  addressCountry?: string;
  categories?: string[];
  services?: string[];
  riskLevel?: SupplierRiskLevel;
  riskScore?: number;
  notes?: string;
}

type NullableSupplierUpdateFields = {
  tradeName: string | null;
  stateRegistration: string | null;
  municipalRegistration: string | null;
  taxRegime: string | null;
  bankName: string | null;
  bankCode: string | null;
  bankAgency: string | null;
  bankAccount: string | null;
  bankAccountType: string | null;
  bankPixKeyType: SupplierPixKeyType | null;
  bankPixKey: string | null;
  addressZipCode: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressDistrict: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressCountry: string | null;
  notes: string | null;
};

export type UpdateSupplierPayload = Partial<
  Omit<CreateSupplierPayload, keyof NullableSupplierUpdateFields>
> & Partial<NullableSupplierUpdateFields> & {
  status?: SupplierStatus;
  homologationStep?: HomologationStep;
  administrativeBlockReason?: string | null;
};

export interface SupplierPortalUpdatePayload {
  tradeName: string | null;
  stateRegistration: string | null;
  municipalRegistration: string | null;
  taxRegime: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  bankName: string | null;
  bankCode: string | null;
  bankAgency: string | null;
  bankAccount: string | null;
  bankAccountType: string | null;
  bankPixKeyType: SupplierPixKeyType | null;
  bankPixKey: string | null;
  addressZipCode: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressDistrict: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressCountry: string | null;
  categories: string[];
  services: string[];
}

export interface CreateSupplierDocumentPayload {
  documentType: string;
  name: string;
  expiresAt?: string;
  issuedAt?: string;
}

export interface CreateSupplierApprovalPayload {
  step: HomologationStep;
  decision: SupplierApprovalDecision;
  justification?: string;
  validUntil?: string;
}

export interface CreateSupplierEvaluationPayload {
  qualityScore: number;
  deadlineScore: number;
  serviceScore: number;
  complianceScore: number;
  priceScore: number;
  slaScore: number;
  comments?: string;
}

export const SUPPLIERS_KEYS = {
  all: ['suppliers'] as const,
  list: (params?: object) => ['suppliers', 'list', params] as const,
  detail: (id: string) => ['suppliers', 'detail', id] as const,
  dashboard: ['suppliers', 'dashboard'] as const,
  documentTypes: ['suppliers', 'document-types'] as const,
  portal: ['suppliers', 'portal', 'me'] as const,
  portalNotifications: (supplierId: string) => ['suppliers', 'portal', supplierId, 'notifications'] as const,
};

export async function listarTiposDocumentoFornecedor(): Promise<SupplierDocumentType[]> {
  const { data } = await api.get<SupplierDocumentType[] | { data?: SupplierDocumentType[] }>(
    '/suppliers/document-types',
  );
  return unwrapList<SupplierDocumentType>(data).filter((documentType) => documentType.active);
}

export async function listarFornecedores(params?: {
  status?: SupplierStatus | '';
  category?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<SupplierListResponse> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.category) query.set('category', params.category);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();
  const { data } = await api.get<SupplierListResponse>(`/suppliers${qs ? `?${qs}` : ''}`);
  return data;
}

export async function obterPortalFornecedor(): Promise<Supplier> {
  const { data } = await api.get<Supplier>('/suppliers/portal/me');
  return data;
}

export async function atualizarPortalFornecedor(payload: SupplierPortalUpdatePayload): Promise<Supplier> {
  const { data } = await api.patch<Supplier>('/suppliers/portal/me', payload);
  return data;
}

export async function listarNotificacoesPortalFornecedor(
  supplierId: string,
): Promise<SupplierPortalNotification[]> {
  const { data } = await api.get<
    SupplierPortalNotification[] | { data?: SupplierPortalNotification[] }
  >('/notifications');
  return unwrapList<SupplierPortalNotification>(data).filter((notification) => (
    notification.payload?.supplierId === supplierId
  ));
}

export async function obterFornecedor(id: string): Promise<Supplier> {
  const { data } = await api.get<Supplier>(`/suppliers/${id}`);
  return data;
}

export async function validarFornecedorEmCompras(supplierId: string) {
  const { data } = await api.get<{
    domain: 'compras';
    rule: string;
    message: string;
    supplierId: string;
    canParticipateInPurchases: boolean;
    status: SupplierStatus;
    blockingReasons: string[];
    blockingConditions: SupplierBlockingCondition[];
  }>(`/purchasing/suppliers/${supplierId}/eligibility`);
  return data;
}

export async function validarFornecedorEmContratos(supplierId: string) {
  const { data } = await api.get<{
    domain: 'contratos';
    rule: string;
    message: string;
    supplierId: string;
    canParticipateInPurchases: boolean;
    status: SupplierStatus;
    blockingReasons: string[];
    blockingConditions: SupplierBlockingCondition[];
  }>(`/contracts/suppliers/${supplierId}/eligibility`);
  return data;
}

export async function obterDashboardFornecedores(): Promise<SupplierDashboard> {
  const { data } = await api.get<{ data: SupplierDashboard }>('/suppliers/dashboard');
  return data.data;
}

export async function criarFornecedor(payload: CreateSupplierPayload): Promise<Supplier> {
  const { data } = await api.post<Supplier>('/suppliers', payload);
  return data;
}

export async function atualizarFornecedor(id: string, payload: UpdateSupplierPayload): Promise<Supplier> {
  const { data } = await api.patch<Supplier>(`/suppliers/${id}`, payload);
  return data;
}

export async function adicionarDocumentoFornecedor(
  id: string,
  payload: CreateSupplierDocumentPayload,
  file: File,
): Promise<Supplier> {
  const formData = new FormData();
  formData.append('documentType', payload.documentType);
  formData.append('name', payload.name);
  formData.append('file', file);
  if (payload.expiresAt) formData.append('expiresAt', payload.expiresAt);
  if (payload.issuedAt) formData.append('issuedAt', payload.issuedAt);
  const { data } = await api.post<Supplier>(`/suppliers/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function revisarDocumentoFornecedor(
  supplierId: string,
  documentId: string,
  payload: { status: 'aprovado' | 'reprovado'; rejectionReason?: string },
): Promise<Supplier> {
  const { data } = await api.patch<Supplier>(`/suppliers/${supplierId}/documents/${documentId}/review`, payload);
  return data;
}

export async function registrarAprovacaoFornecedor(id: string, payload: CreateSupplierApprovalPayload): Promise<Supplier> {
  const { data } = await api.post<Supplier>(`/suppliers/${id}/approvals`, payload);
  return data;
}

export async function registrarAvaliacaoFornecedor(id: string, payload: CreateSupplierEvaluationPayload): Promise<Supplier> {
  const { data } = await api.post<Supplier>(`/suppliers/${id}/evaluations`, payload);
  return data;
}
